import ReactModal from 'react-modal';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import './TestModal.scss';
import { useSelector } from 'react-redux';
import { selectControls } from '../../../redux/controls';
import { useCallback, useEffect, useState } from 'react';
import { QuizLaunchRequest, TestModalProps } from '../models/Modal.interfaces';
import Spinner from '../../Spinner/Spinner';
import { ERROR_RESPONSE, ErrorResponse, GRADE_LIST_PROP, GRADE_PROP, IGrade, isType, ISubjectGradeInfo, SUBJECT_PROP } from '../../../shared';
import { fetchQuizLaunch, fetchQuizSelectors } from '../models';
import { TestLaunchResult, TEST_LAUNCH_RESULT } from '../../TestCodeButton/models';
import { genDDMenuItemLabel } from '../../QueryPanel/models';
import { cloneDeep } from 'lodash';
import ga4 from '../../../shared/analyticsEvents';
import heroImage from '../../../images/takeNAEPnow_homePage_hero.png';

const staticMenuStyle = {
  width: '95%'
};

function TestModal({open, onClose, subject, cohort}: TestModalProps): JSX.Element {
  const controls = useSelector(selectControls);
  ReactModal.setAppElement('#root');

  // Quiz selectors
  const [subjects, setSubjects] = useState<ISubjectGradeInfo[]>([]);
  const [grades, setGrades] = useState<IGrade[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<ISubjectGradeInfo|null>(null);
  const [selectedGrade, setSelectedGrade] = useState<IGrade|null>(null);

  // State that effects UI
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [dropdownAbortController, setDropdownAbortController] = useState<AbortController|null>(null);

  //#region Function Callbacks

  const resetState = useCallback(() => {
    subjects && subjects.forEach(subj => subj.isSelected && (subj.isSelected = false));
    setSelectedSubject(null);
    setSelectedGrade(null);
    setErrorMessage('');
    setLoading(false);
  }, [subjects]);

  const onClickStartQuiz = useCallback(async(subject: ISubjectGradeInfo|null, grade: IGrade|null): Promise<void> => {
    if (subject == null || grade == null) return;
    const request: QuizLaunchRequest = {
      subjectCode: subject.subject,
      grade: grade.grade,
      ndeSystemID: + subject.ndeSystemID,
      numQuizQuestions: 5
    };
    ga4.onGA4TakeQuiz(subject?.label || 'Unknown');
    setLoading(true);
    const launchResponse = await fetchQuizLaunch(request);
    if (isType<TestLaunchResult>(launchResponse, TEST_LAUNCH_RESULT)) {
      setLoading(false);
      try {
        new URL(launchResponse.startTestURL);
        setLoading(false);
        window.location.href = launchResponse.startTestURL;
      } catch (err) {
        setErrorMessage('Error launching quiz, invalid URL received');
        console.error(err);
        setLoading(false);
      }
    } else {
      setErrorMessage(launchResponse.errMsg);
      setLoading(false);
    }
  }, []);

  const startBtnClickHandler = useCallback(() => {
    if (selectedSubject && selectedGrade) {
      onClickStartQuiz(selectedSubject, selectedGrade);
    }
  }, [onClickStartQuiz, selectedGrade, selectedSubject]);

  const onCloseHandler = useCallback(() => {
    resetState();
    onClose(true);
  }, [onClose, resetState]);

  /**
 * onClick Handler for subject dropdown menu
 */
  const dropdownOnClickHandler = useCallback((e: React.FormEvent, field: string, obj: ISubjectGradeInfo|IGrade) => {
    e.preventDefault();
    if (obj && typeof obj !== null) {
      switch(field) {
      case 'subject':
        if (SUBJECT_PROP in obj && GRADE_LIST_PROP in obj) {
          if (
            !selectedSubject ||
            (obj.subject !== selectedSubject?.subject || obj.ndeSystemID !== selectedSubject.ndeSystemID)
          ) {
            subjects.forEach(sub => sub.isSelected && (sub.isSelected = false));
            obj.isSelected = true;
            const clonedSubject = cloneDeep(obj);
            setSelectedSubject(clonedSubject);
            if (selectedGrade && clonedSubject) {
              // set grade as selected if exists in new subject list of grades
              clonedSubject.gradeList.forEach(gd => gd.isSelected = false);
              const gradeFoundinNewSubject = clonedSubject.gradeList.find(
                gd => gd.cohort === selectedGrade.cohort
              );
              if (gradeFoundinNewSubject) {
                gradeFoundinNewSubject.isSelected = true;
              }
            }
            if (clonedSubject.gradeList.filter(gd => gd.isSelected).length === 1) {
              setGrades(clonedSubject.gradeList);
              const gradeFound = clonedSubject.gradeList.find(gd => gd.isSelected);
              gradeFound && setSelectedGrade(gradeFound);
            } else {
              const grages = clonedSubject.gradeList.map((gd, ind) => {
                gd.isSelected = ind === 0;
                return gd;
              });
              setGrades(grages);
              setSelectedGrade(grages[0]);
            }
          }
        }
        break;
      case 'grade':
        if (GRADE_PROP in obj) {
          if (
            !selectedGrade ||
            (obj.grade !== selectedGrade.grade)
          ) {
            if (grades.length) {
              setGrades(grades.map(gd => {
                gd.isSelected = obj.grade === gd.grade;
                return gd;
              }));
              setSelectedGrade(obj);
            }
          }
        }
        break;
      default:
        console.log('Option can not be handled by ddhander');
      }
    }
  }, [grades, selectedGrade, selectedSubject, subjects, setGrades]);

  //#endregion Function Callbacks

  //#region useEffects

  useEffect(() => {
    return () => {
      dropdownAbortController?.abort();
    };
  }, [dropdownAbortController]);


  useEffect(() => {
    if (open && !subjects.length) {
      setLoading(true);
      setErrorMessage('');
      if (dropdownAbortController) dropdownAbortController.abort();
      const dropdownAbort = new AbortController();
      setDropdownAbortController(dropdownAbort);
      fetchQuizSelectors(dropdownAbort)
        .then((response) => {
          if (!isType<ErrorResponse>(response, ERROR_RESPONSE)) {
            const cloneResponse = cloneDeep(response);
            // if we have a subject passed in, select it
            if (subject) {
              const paramSubjectFound = cloneResponse.find(sub => sub.subject === subject);
              if (paramSubjectFound) {
                paramSubjectFound.isSelected = true;
              }
            }
            setSubjects(cloneResponse);
            const subjectFound = cloneResponse.find(sub => sub.isSelected);
            if (subjectFound) {
              if (cohort) {
                const gradeFound = subjectFound.gradeList.find(gd => gd.cohort === Number.parseInt(cohort));
                if (gradeFound) {
                  gradeFound.isSelected = true;
                  setGrades(subjectFound.gradeList);
                  setTimeout(() => {
                    onClickStartQuiz(subjectFound, gradeFound);
                  },100);
                  setLoading(false);
                  return;
                }
              }
              setGrades(subjectFound.gradeList);
            } else if (cohort) {
              // get a list of all unique grades by unique cohort from the cloneResponse for the grade dropdown
              const gradeList = cloneResponse.reduce((acc, sub) => {
                sub.gradeList.forEach(gd => {
                  if (!acc.find(agd => agd.cohort === gd.cohort)) {
                    acc.push(gd);
                  }
                });
                return acc.sort((a, b) => a.cohort - b.cohort);
              }, [] as IGrade[]);
              // is cohort a valid grade?
              const gradeFound = gradeList.find(gd => gd.cohort === Number.parseInt(cohort));
              if (gradeFound) {
                gradeFound.isSelected = true;
                setSelectedGrade(gradeFound);
              }

              setGrades(gradeList);
            }
          } else if (response.errMsg === 'Request cancelled') {
            return;
          } else {
            alert('Error fetching dropdowns');
          }
          setLoading(false);
        })
        .catch((err) => {
          alert('Error fetching dropdowns');
          console.log(err);
          setLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (subjects.length) {
      const subjectFound = subjects.find(sub => sub.isSelected);
      if (subjectFound) {
        const gradeFound = subjectFound.gradeList.find(gd => gd.isSelected);
        setSelectedSubject(subjectFound);
        if (gradeFound) {
          setSelectedGrade(gradeFound);
        }
      }
    }
  }, [subjects]);

  //#endregion useEffects

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal nqt-test-modal"
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          shouldReturnFocusAfterClose={true}
          contentLabel={'National Assessment Of Educational Progress'}
        >
          <div id='nqt-modal-takequiz' className='nqt-modal--container container'>
            <img src={heroImage} alt={controls?.TakeNAEPNowImagePopupQuizModal?.controlText} className="nqt-modal--header-image" />
            <div className="row justify-content-end">
              <div className="test-modal--header-buttons col-1 offset-11">
                <button className='test-modal--close-button position-absolute top-0 end-0'
                  ref={(el) => open && el?.focus()}
                  onClick={onCloseHandler}>
                  <DeleteIcon/>
                  <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                </button>
              </div>
            </div>
            <div>
              <div className='test-modal--spincontainer'>
                { loading && <Spinner size={100} containerClassName='test-modal--spincontainer' />}
              </div>
              <div className="test-modal--title row">
                <div className='col-4 offset-8 p-0'>
                  <h2 className='nqt-modal--header'>{controls?.TryHeader?.controlText}</h2>
                </div>
                <div className="test-modal--subject col-4 offset-8">
                  <div className="dropdown">
                    <button className="nqt-dropdown-btn btn dropdown-toggle"
                      disabled={!subjects.length}
                      type="button"
                      id="tm-ddSub"
                      data-bs-toggle="dropdown"
                      aria-expanded="false">
                      <span className='dropdown-interior test-modal--dd-interior justify-self-center'>
                        {
                          selectedSubject ?
                            selectedSubject.label :
                            controls?.Subject?.controlText
                        }
                      </span>
                      <span className='dropdown-arrow justify-self-end end-0'></span>
                    </button>
                    <ul className="dropdown-menu"
                      style={staticMenuStyle}
                      aria-labelledby="tm-ddSub">
                      {genDDMenuItemLabel(controls?.Subject?.controlText)}
                      {
                        subjects.length > 0 &&
                          subjects.map((subObj, ind) => (
                            <li key={`sq-sub-${ind}`}>
                              <a className={'dropdown-item' + (subObj.isSelected ? ' selected' : '')} onClick={(e) => dropdownOnClickHandler(e, 'subject', subObj)} href="#">
                                {subObj.label}
                              </a>
                            </li>
                          ))
                      }
                    </ul>
                  </div>
                </div>
              </div>
              <div className="row justify-content-center">
                <div className="test-modal--grade col-4 offset-8">
                  <div className="dropdown">
                    <button className="nqt-dropdown-btn btn dropdown-toggle d-flex justify-content-center align-items-center"
                      disabled={ !grades.length && !selectedGrade}
                      type="button"
                      id="tm-ddGrade"
                      data-bs-toggle="dropdown"
                      aria-expanded="false">
                      <span className='dropdown-interior test-modal--dd-interior'>
                        {
                          selectedGrade ?
                            selectedGrade.grade :
                            (selectedSubject && selectedSubject.ndeSystemID === '3') ?
                              controls?.Age?.controlText :
                              controls?.Grade?.controlText
                        }
                      </span>
                      <span className='dropdown-arrow'></span>
                    </button>
                    <ul className="dropdown-menu"
                      style={staticMenuStyle}
                      aria-labelledby="tm-ddGrade">
                      {
                        (selectedSubject && selectedSubject.ndeSystemID === '3') ?
                          genDDMenuItemLabel(controls?.Age.controlText) :
                          genDDMenuItemLabel(controls?.Grade?.controlText)
                      }
                      {
                        grades.length > 0 &&
                                grades.map((gdObj, ind) => (
                                  <li key={`tm-grade-${ind}`}>
                                    <a className={'dropdown-item' + (gdObj.isSelected ? ' selected' : '')}
                                      onClick={(e) => dropdownOnClickHandler(e, 'grade', gdObj)} href="#">
                                      {gdObj.grade}
                                    </a>
                                  </li>
                                ))
                      }
                    </ul>
                  </div>
                </div>
              </div>
              <div className="row test-modal-row-btn">
                <div className="col-4 offset-8 d-flex justify-content-center align-items-center">
                  <button className='btn nqt-btn'
                    onClick={startBtnClickHandler}
                    disabled={!selectedGrade || !selectedSubject}>
                  Start Quiz
                  </button>
                </div>
              </div>
            </div>
            {!!errorMessage &&
            <div className="row">
              <div className="col-2 offset-9 d-flex justify-content-center align-items-center">
                <span className='error-message'>
                  {errorMessage}
                </span>
              </div>
            </div>
            }
            <div className="row tm-hero-text">
              <div className="col-5 h1 position-absolute bottom-0 start-0">
                <span className='d-flex justify-content-center align-items-center'>Take NAEP Now</span>
              </div>
            </div>
          </div>
        </ReactModal>
      }
    </>
  );
}

export default TestModal;